/**
 * Taste events + local profile (Stage B foundation).
 * Device-local by default. Optional POST if window.theme.tasteEndpoint is set.
 * Never throws into callers; safe no-op when storage is blocked.
 */
(function () {
  "use strict";

  var EVENTS_KEY = "theartfolio_taste_events";
  var FEATURES_KEY = "theartfolio_product_features";
  var PROFILE_KEY = "theartfolio_taste_profile";
  var ANON_KEY = "theartfolio_taste_anon";
  var MAX_EVENTS = 200;
  var MAX_FEATURES = 120;
  var VERSION = 1;

  function storageOk() {
    try {
      var probe = EVENTS_KEY + "_probe";
      localStorage.setItem(probe, "1");
      localStorage.removeItem(probe);
      return true;
    } catch (error) {
      return false;
    }
  }

  var usable = storageOk();

  function readJson(key, fallback) {
    if (!usable) return fallback;
    try {
      var parsed = JSON.parse(localStorage.getItem(key) || "null");
      return parsed == null ? fallback : parsed;
    } catch (error) {
      return fallback;
    }
  }

  function writeJson(key, value) {
    if (!usable) return false;
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function anonId() {
    if (!usable) return "";
    var existing = "";
    try { existing = localStorage.getItem(ANON_KEY) || ""; } catch (error) {}
    if (existing) return existing;
    var id = "anon-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
    try { localStorage.setItem(ANON_KEY, id); } catch (error) {}
    return id;
  }

  function cleanHandle(value) {
    return String(value == null ? "" : value).trim().toLowerCase();
  }

  function asList(value) {
    if (Array.isArray(value)) {
      return value.map(function (item) {
        return String(item && (item.label || item.value || item) || "").trim();
      }).filter(Boolean);
    }
    if (value == null || value === "") return [];
    return String(value).split(/[\s,]+/).map(function (part) {
      return part.trim();
    }).filter(Boolean);
  }

  function normaliseFeatures(raw) {
    if (!raw || typeof raw !== "object") return null;
    var handle = cleanHandle(raw.handle || raw.id || raw.product || "");
    if (!handle || /^\d+$/.test(handle)) return null;
    return {
      handle: handle,
      moods: asList(raw.moods).map(function (m) { return m.toLowerCase(); }),
      colours: asList(raw.colours),
      genre: String(raw.genre || raw.type || "").trim(),
      orientation: String(raw.orientation || "").trim(),
      updatedAt: Date.now()
    };
  }

  function getFeaturesMap() {
    var map = readJson(FEATURES_KEY, {});
    return map && typeof map === "object" && !Array.isArray(map) ? map : {};
  }

  function rememberFeatures(raw) {
    var features = normaliseFeatures(raw);
    if (!features) return null;
    var map = getFeaturesMap();
    var prev = map[features.handle] || {};
    map[features.handle] = {
      handle: features.handle,
      moods: features.moods.length ? features.moods : asList(prev.moods),
      colours: features.colours.length ? features.colours : asList(prev.colours),
      genre: features.genre || prev.genre || "",
      orientation: features.orientation || prev.orientation || "",
      updatedAt: Date.now()
    };
    var keys = Object.keys(map);
    if (keys.length > MAX_FEATURES) {
      keys.sort(function (a, b) {
        return (map[a].updatedAt || 0) - (map[b].updatedAt || 0);
      });
      keys.slice(0, keys.length - MAX_FEATURES).forEach(function (key) {
        delete map[key];
      });
    }
    writeJson(FEATURES_KEY, map);
    return map[features.handle];
  }

  function featuresFromDom(handle) {
    handle = cleanHandle(handle);
    if (!handle) return null;
    var node = document.querySelector(
      '[data-reaction-id="' + handle + '"], [data-mood-card][data-reaction-id="' + handle + '"]'
    );
    if (!node) {
      node = document.querySelector('[data-moods][data-reaction-id="' + CSSEscape(handle) + '"]');
    }
    // Fallback without CSS.escape for older browsers
    if (!node) {
      var nodes = document.querySelectorAll("[data-reaction-id][data-moods], [data-mood-card][data-moods]");
      for (var i = 0; i < nodes.length; i++) {
        if (cleanHandle(nodes[i].getAttribute("data-reaction-id")) === handle) {
          node = nodes[i];
          break;
        }
      }
    }
    if (!node) return null;
    return rememberFeatures({
      handle: handle,
      moods: node.getAttribute("data-moods") || "",
      genre: node.getAttribute("data-genre") || "",
      orientation: node.getAttribute("data-orientation") || "",
      colours: node.getAttribute("data-colours") || ""
    });
  }

  function CSSEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/["\\]/g, "\\$&");
  }

  function getEvents() {
    var events = readJson(EVENTS_KEY, []);
    return Array.isArray(events) ? events : [];
  }

  function rebuildProfile() {
    var events = getEvents();
    var features = getFeaturesMap();
    var reactions = window.TheArtfolioReactions
      ? (window.TheArtfolioReactions.getAllReactions() || {})
      : {};
    var likes = [];
    var dislikes = [];
    Object.keys(reactions).forEach(function (id) {
      var handle = cleanHandle(id);
      if (!handle || /^\d+$/.test(handle)) return;
      if (reactions[id] === "like") likes.push(handle);
      if (reactions[id] === "dislike") dislikes.push(handle);
    });

    var moods = {};
    var colours = {};
    var genres = {};
    var orientations = {};
    var recentHandles = [];

    function bump(map, key, weight) {
      key = String(key || "").trim().toLowerCase();
      if (!key) return;
      map[key] = (map[key] || 0) + (weight || 1);
    }

    function absorb(handle, weight) {
      var feat = features[handle];
      if (!feat) return;
      asList(feat.moods).forEach(function (mood) { bump(moods, mood, weight); });
      asList(feat.colours).forEach(function (colour) { bump(colours, colour, weight); });
      if (feat.genre) bump(genres, feat.genre, weight);
      if (feat.orientation) bump(orientations, feat.orientation, weight);
    }

    likes.forEach(function (handle) { absorb(handle, 3); });
    dislikes.forEach(function (handle) { absorb(handle, -2); });

    events.slice(-80).forEach(function (event) {
      if (!event || !event.handle) return;
      recentHandles.push(event.handle);
      var weight = event.type === "like" || event.type === "favourite" ? 2
        : event.type === "view" || event.type === "discover_pick" ? 1
        : event.type === "dislike" ? -1
        : 0.5;
      absorb(event.handle, weight);
      if (event.features) absorbFeaturesInline(event.features, weight);
    });

    function absorbFeaturesInline(feat, weight) {
      asList(feat.moods).forEach(function (mood) { bump(moods, mood, weight); });
      asList(feat.colours).forEach(function (colour) { bump(colours, colour, weight); });
      if (feat.genre) bump(genres, feat.genre, weight);
      if (feat.orientation) bump(orientations, feat.orientation, weight);
    }

    // unique recent, newest first
    var seen = {};
    recentHandles = recentHandles.reverse().filter(function (handle) {
      if (seen[handle]) return false;
      seen[handle] = true;
      return true;
    }).slice(0, 24);

    var profile = {
      version: VERSION,
      updatedAt: Date.now(),
      anonId: anonId(),
      likes: likes,
      dislikes: dislikes,
      moods: moods,
      colours: colours,
      genres: genres,
      orientations: orientations,
      recentHandles: recentHandles,
      eventCount: events.length
    };
    writeJson(PROFILE_KEY, profile);
    return profile;
  }

  function getProfile() {
    var profile = readJson(PROFILE_KEY, null);
    if (profile && profile.version === VERSION) return profile;
    return rebuildProfile();
  }

  function scoreHandle(handle, profile) {
    handle = cleanHandle(handle);
    profile = profile || getProfile();
    if (!handle || !profile) return 0;
    if ((profile.dislikes || []).indexOf(handle) !== -1) return -1000;
    var features = getFeaturesMap()[handle];
    if (!features) {
      if ((profile.likes || []).indexOf(handle) !== -1) return 10;
      if ((profile.recentHandles || []).indexOf(handle) !== -1) return 2;
      return 0;
    }
    var score = 0;
    asList(features.moods).forEach(function (mood) {
      score += (profile.moods && profile.moods[mood]) || 0;
    });
    asList(features.colours).forEach(function (colour) {
      var key = String(colour).toLowerCase();
      score += (profile.colours && profile.colours[key]) || 0;
    });
    if (features.genre) {
      score += ((profile.genres && profile.genres[String(features.genre).toLowerCase()]) || 0) * 1.5;
    }
    if (features.orientation) {
      score += (profile.orientations && profile.orientations[String(features.orientation).toLowerCase()]) || 0;
    }
    if ((profile.likes || []).indexOf(handle) !== -1) score += 8;
    return score;
  }

  function rankHandles(handles) {
    var profile = getProfile();
    return (handles || []).slice().sort(function (a, b) {
      return scoreHandle(b, profile) - scoreHandle(a, profile);
    });
  }

  function maybeBeacon(event) {
    var endpoint = window.theme && window.theme.tasteEndpoint;
    if (!endpoint || !event) return;
    endpoint = String(endpoint).trim();
    if (!endpoint) return;
    var payload = {
      anonId: anonId(),
      event: event,
      path: location.pathname,
      ts: event.ts
    };
    try {
      var body = JSON.stringify(payload);
      // text/plain avoids a CORS preflight so sendBeacon works reliably.
      if (navigator.sendBeacon) {
        var queued = navigator.sendBeacon(endpoint, new Blob([body], { type: "text/plain" }));
        if (queued) return;
      }
      fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: body,
        keepalive: true,
        credentials: "omit",
        mode: "cors"
      }).catch(function () {});
    } catch (error) {
      // Swallow — taste capture must never break shopping.
    }
  }

  function track(type, detail) {
    try {
      type = String(type || "").trim().toLowerCase();
      if (!type) return false;
      detail = detail || {};
      var handle = cleanHandle(detail.handle || detail.id || detail.product || "");
      if (handle && (detail.moods || detail.colours || detail.genre || detail.orientation)) {
        rememberFeatures(Object.assign({ handle: handle }, detail));
      } else if (handle) {
        featuresFromDom(handle);
      }

      var event = {
        type: type,
        handle: handle || null,
        ts: Date.now(),
        path: location.pathname
      };
      if (detail.options && typeof detail.options === "object") {
        event.options = {
          frameStyle: detail.options.frameStyle || "",
          colour: detail.options.colour || "",
          size: detail.options.size || "",
          mount: detail.options.mount || "",
          mountColour: detail.options.mountColour || "",
          scene: detail.options.scene || ""
        };
      }
      var feat = handle ? getFeaturesMap()[handle] : null;
      if (feat) {
        event.features = {
          moods: feat.moods || [],
          colours: feat.colours || [],
          genre: feat.genre || "",
          orientation: feat.orientation || ""
        };
      }

      var events = getEvents();
      events.push(event);
      if (events.length > MAX_EVENTS) events = events.slice(events.length - MAX_EVENTS);
      writeJson(EVENTS_KEY, events);
      rebuildProfile();
      maybeBeacon(event);
      return true;
    } catch (error) {
      return false;
    }
  }

  function bootstrapFromExisting() {
    if (!usable) return;
    var events = getEvents();
    if (events.length) {
      rebuildProfile();
      return;
    }
    var reactions = window.TheArtfolioReactions
      ? (window.TheArtfolioReactions.getAllReactions() || {})
      : {};
    Object.keys(reactions).forEach(function (id) {
      var handle = cleanHandle(id);
      if (!handle || /^\d+$/.test(handle)) return;
      featuresFromDom(handle);
      track(reactions[id], { handle: handle, bootstrap: true });
    });
    if (window.TheArtfolioFavourites) {
      try {
        (window.TheArtfolioFavourites.getFavourites() || []).slice(0, 20).forEach(function (favourite) {
          var handle = cleanHandle(favourite.handle || favourite.productKey || "");
          if (!handle) return;
          rememberFeatures({
            handle: handle,
            moods: favourite.moods,
            colours: favourite.colours,
            genre: favourite.genre,
            orientation: favourite.orientation
          });
          track("favourite", {
            handle: handle,
            options: favourite.options,
            bootstrap: true
          });
        });
      } catch (error) {}
    }
    rebuildProfile();
  }

  function bindListeners() {
    document.addEventListener("theartfolio:reactionchange", function (event) {
      var detail = (event && event.detail) || {};
      if (detail.resetDislikes) {
        track("reset_dislikes", {});
        return;
      }
      if (!detail.id) return;
      var type = detail.reaction === "like" ? "like"
        : detail.reaction === "dislike" ? "dislike"
        : "reaction_clear";
      track(type, { handle: detail.id });
    });

    document.addEventListener("discover:pick", function (event) {
      var detail = (event && event.detail) || {};
      var product = detail.product || {};
      if (!product.handle) return;
      rememberFeatures(product);
      track("discover_pick", {
        handle: product.handle,
        moods: product.moods,
        colours: product.colours,
        genre: product.genre,
        orientation: product.orientation,
        options: detail.options
      });
    });

    document.addEventListener("theartfolio:productview", function (event) {
      var detail = (event && event.detail) || {};
      if (!detail.handle) return;
      track("view", detail);
    });
  }

  function trackProductPageView() {
    if (!document.body || !/\btemplate-product\b/.test(document.body.className)) return;
    var dataEl = document.querySelector("[data-product-json]");
    if (!dataEl) return;
    try {
      var product = JSON.parse(dataEl.textContent);
      if (!product || !product.handle) return;
      var match = location.pathname.match(/\/products\/([^/?#]+)/);
      var handle = match ? decodeURIComponent(match[1]) : product.handle;
      track("view", { handle: handle });
    } catch (error) {}
  }

  function trackAddToCart() {
    document.addEventListener("submit", function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      var handle = "";
      var match = location.pathname.match(/\/products\/([^/?#]+)/);
      if (match) handle = decodeURIComponent(match[1]);
      if (!handle) {
        var json = document.querySelector("[data-product-json]");
        try {
          handle = json ? (JSON.parse(json.textContent).handle || "") : "";
        } catch (error) {}
      }
      track("add_to_cart", { handle: handle });
    }, true);
  }

  window.TheArtfolioTaste = {
    track: track,
    getEvents: getEvents,
    getProfile: getProfile,
    rebuildProfile: rebuildProfile,
    rememberFeatures: rememberFeatures,
    getFeatures: function (handle) {
      return getFeaturesMap()[cleanHandle(handle)] || null;
    },
    scoreHandle: scoreHandle,
    rankHandles: rankHandles,
    anonId: anonId
  };

  function boot() {
    bindListeners();
    bootstrapFromExisting();
    trackProductPageView();
    trackAddToCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
